import React, {
	useContext,
	useEffect,
	useState,
	useRef,
	useLayoutEffect
} from 'react';
import { useTranslation } from 'react-i18next';

import * as S from './styles';
import { color } from 'styles/pallete';
import Modal from 'components/Modal';
import ItensMenuContext from 'context/ItensMenuContext';
import { RedirectValues } from 'types/RedirectItemMenuProps';
import SimpleInput from 'components/SimpleInput';
import { UseTranslationProps } from 'types/UseTranslationProps';
import SelectInput from 'components/SelectInput';
import { redirectTypes } from './options';
import { SelectInputItemProps } from 'types/SelectInputItemProps';
import InputImgBigger from 'components/InputImgBigger';
import InputColor from 'components/InputColor';
import Check from 'components/Buttons/Check';
import { addNewElementIdOnBack, editElementIdOnBack } from './reqs';
import { DataFormProps } from './typeForm';
import SystemNotifyContext from 'context/SystemNotifyContext';
import { verifyIfCanSave } from './function';
import { returnElementToSelectComponentByName } from 'utils/functions';
import { getElementsId } from 'pages/ItemMenuSelected/reqs';
import { ImgSavedProps } from 'types/ImgSavedProps';

const initialDataForm: DataFormProps = {
	name: '',
	typeRedirect: { id: '', option: '' },
	valueRedirect: '',
	leftImg: null,
	rightImg: null,
	banner: null,
	isInputUrlInput: false,
	urlVideo: '',
	valueRedirectBanner: '',
	color: '#000',
	active: false,
	isAllwaysOpen: false
};

interface FormItemMenuSelectedProps {
	slug: string;
}

export default function FormItemMenuSelected({
	slug
}: FormItemMenuSelectedProps) {
	const isMounted = useRef(false);
	const { t }: UseTranslationProps = useTranslation();

	const {
		isFormOpen,
		setIsFormOpen,
		setItensFromElement,
		itemFromElementToEdit,
		setItemFromElementToEdit,
		setIdFatherElement,
		idFatherElement,
		setIsLoad
	} = useContext(ItensMenuContext);

	const { handleNotify } = useContext(SystemNotifyContext);
	const [dataForm, setDataForm] = useState<DataFormProps>(initialDataForm);

	function returnOptionsToList(
		olderList: { label: string; value: RedirectValues }[]
	) {
		const newList: SelectInputItemProps[] = [];

		if (olderList.length > 0) {
			olderList.forEach((item) => {
				newList.push({ id: item.value, option: item.label });
			});
		}

		return newList;
	}

	async function addNewItemMenu() {
		const canSave = verifyIfCanSave(dataForm, handleNotify);

		if (canSave) {
			const { data, isSuccess } = await addNewElementIdOnBack(
				dataForm,
				slug,
				handleNotify,
				idFatherElement
			);

			if (isSuccess && data !== null) {
				setIsLoad(true);
				const resp = await getElementsId(slug, handleNotify);
				setIsLoad(false);

				if (resp.isSuccess && resp.data.length > 0)
					setItensFromElement(resp.data);
				setIsFormOpen(false);
			}
		}
	}

	async function editItemMenu() {
		const canSave = verifyIfCanSave(dataForm, handleNotify);

		if (itemFromElementToEdit === null) {
			handleNotify('Item a ser editado vázio', 'error');
			return;
		}

		if (canSave) {
			const { data, isSuccess } = await editElementIdOnBack(
				itemFromElementToEdit,
				dataForm,
				handleNotify
			);

			if (isSuccess && data !== null) {
				setIsLoad(true);
				const resp = await getElementsId(slug, handleNotify);
				setIsLoad(false);

				if (resp.isSuccess && resp.data.length > 0)
					setItensFromElement(resp.data);
				setIsFormOpen(false);
			}
		}
	}

	function resetStates() {
		setIsFormOpen(false);
		setItemFromElementToEdit(null);
		setIdFatherElement('');
	}

	//em modo de edição
	useLayoutEffect(() => {
		if (itemFromElementToEdit !== null) {
			const redirectType = returnElementToSelectComponentByName(
				returnOptionsToList(redirectTypes),
				itemFromElementToEdit.redirect_type
			);

			setDataForm((state) => ({
				...state,
				name: itemFromElementToEdit.name,
				valueRedirect:
					itemFromElementToEdit.redirect_value === null
						? ''
						: itemFromElementToEdit.redirect_value,
				color:
					itemFromElementToEdit.highlighted_color === null
						? state.color
						: itemFromElementToEdit.highlighted_color,
				urlVideo: itemFromElementToEdit.reference_value,
				valueRedirectBanner:
					itemFromElementToEdit.banner_redirect_value === null
						? ''
						: itemFromElementToEdit.banner_redirect_value,
				active: itemFromElementToEdit.active,
				isAllwaysOpen: itemFromElementToEdit.always_open,
				typeRedirect:
					redirectType === null ? initialDataForm.typeRedirect : redirectType
			}));
		}
	}, []);

	//executa somente quando o componente é desmotado
	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;

			return;
		}

		return () => {
			resetStates();
		};
	}, []);

	return (
		<Modal
			isOpenProps={isFormOpen}
			setIsOpenModal={() => {
				setIsFormOpen((value) => !value);
				resetStates();
			}}
			title={t('formItemMenuSelected.newItemMenu')}
			buttonClose={false}
		>
			<S.container>
				<S.boxInputs>
					<S.title>{t('formItemMenuSelected.settings')}</S.title>
					<S.twoInputs>
						<SimpleInput
							label={t('formItemMenuSelected.name')}
							initialValue={dataForm.name}
							getValue={(value: string) => {
								setDataForm((obj) => ({ ...obj, name: value }));
							}}
						/>
						<SelectInput
							label={t('formItemMenuSelected.typeRedirect')}
							list={returnOptionsToList(redirectTypes)}
							initialValue={
								dataForm?.typeRedirect.option === ''
									? ''
									: dataForm?.typeRedirect
							}
							getValue={(value: SelectInputItemProps) => {
								setDataForm((obj) => ({
									...obj,
									typeRedirect: value
								}));
							}}
						/>
					</S.twoInputs>

					<SimpleInput
						label={t('formItemMenuSelected.redirectValue')}
						initialValue={dataForm.valueRedirect}
						getValue={(value: string) => {
							setDataForm((obj) => ({ ...obj, valueRedirect: value }));
						}}
					/>

					<S.title>{t('formItemMenuSelected.illustrationItens')}</S.title>

					<S.boxImgs>
						<InputImgBigger
							idImagem="leftImg"
							label={t('formItemMenuSelected.imgLeft')}
							initialImage={
								dataForm?.leftImg?.relativePath
									? `${process.env.REACT_APP_PATH_SERVER_IMGS}${dataForm?.leftImg?.relativePath}`
									: ''
							}
							getImage={(value: ImgSavedProps) => {
								setDataForm((obj) => ({
									...obj,
									leftImg: value
								}));
							}}
						/>
						<InputImgBigger
							idImagem="banner"
							label={t('formItemMenuSelected.banner')}
							initialImage={
								dataForm?.banner?.relativePath
									? `${process.env.REACT_APP_PATH_SERVER_IMGS}${dataForm?.banner?.relativePath}`
									: ''
							}
							getImage={(value: ImgSavedProps) => {
								setDataForm((obj) => ({
									...obj,
									banner: value
								}));
							}}
							doubtBox
							messageDoubtBox={t('formItemMenuSelected.bannerNotice')}
						/>
						<InputImgBigger
							idImagem="rigthImg"
							label={t('formItemMenuSelected.imgRigth')}
							initialImage={
								dataForm?.rightImg?.relativePath
									? `${process.env.REACT_APP_PATH_SERVER_IMGS}${dataForm?.rightImg?.relativePath}`
									: ''
							}
							getImage={(value: ImgSavedProps) => {
								setDataForm((obj) => ({
									...obj,
									rightImg: value
								}));
							}}
						/>
					</S.boxImgs>

					<S.twoInputs>
						<InputColor
							label={t('formItemMenuSelected.color')}
							value={dataForm.color || '#000'}
							setValue={(value: string) => {
								setDataForm((obj) => ({
									...obj,
									color: value
								}));
							}}
							rgbOrExadecimal={false}
						/>

						<SimpleInput
							label={t('formItemMenuSelected.redirectBanner')}
							initialValue={dataForm.valueRedirectBanner}
							getValue={(value: string) => {
								setDataForm((obj) => ({
									...obj,
									valueRedirectBanner: value
								}));
							}}
						/>
					</S.twoInputs>

					<S.checksBox>
						<Check
							setIsChecked={() => {
								setDataForm((obj) => ({
									...obj,
									active: !obj.active
								}));
							}}
							isChecked={dataForm.active}
							text={t('formItemMenuSelected.active')}
							colorCheck={color.interface.green2}
						/>

						<Check
							setIsChecked={() => {
								setDataForm((obj) => ({
									...obj,
									isAllwaysOpen: !obj.isAllwaysOpen
								}));
							}}
							isChecked={dataForm.isAllwaysOpen}
							text={t('formItemMenuSelected.allwaysOpen')}
							colorCheck={color.interface.green2}
						/>

						<Check
							setIsChecked={() => {
								setDataForm((obj) => ({
									...obj,
									isInputUrlInput: !obj.isInputUrlInput
								}));
							}}
							isChecked={dataForm.isInputUrlInput}
							text={t('formItemMenuSelected.addVideo')}
							colorCheck={color.interface.green2}
						/>
					</S.checksBox>

					{dataForm.isInputUrlInput && (
						<SimpleInput
							styleInput={{ paddingBottom: '2rem' }}
							label={t('formItemMenuSelected.urlVideo')}
							initialValue={dataForm.urlVideo}
							getValue={(value: string) => {
								setDataForm((obj) => ({ ...obj, urlVideo: value }));
							}}
						/>
					)}
				</S.boxInputs>

				<S.btns>
					<S.cancel
						onClick={() => {
							resetStates();
						}}
					>
						{t('cancel')}
					</S.cancel>
					<S.save
						onClick={
							itemFromElementToEdit === null ? addNewItemMenu : editItemMenu
						}
					>
						{t('save')}
					</S.save>
				</S.btns>
			</S.container>
		</Modal>
	);
}
